import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, CreditCard as Edit, Copy, Trash2, Eye, Calendar } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { AdminLayout } from '../../components/admin/layout/AdminLayout';
import { Button } from '../../components/admin/ui/Button';
import { Card } from '../../components/admin/ui/Card';
import { Input } from '../../components/admin/ui/Input';
import { Badge } from '../../components/admin/ui/Badge';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'scheduled' | 'published';
  published_at: string | null;
  updated_at: string;
  author_name: string;
  categories: string[];
}

export function BlogPostsPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    fetchPosts();
  }, [searchQuery, statusFilter]);

  async function fetchPosts() {
    setLoading(true);

    let query = supabase
      .from('blog_posts')
      .select(`
        id,
        title,
        slug,
        status,
        published_at,
        updated_at,
        author_id,
        user_profiles!blog_posts_author_id_fkey (
          first_name,
          last_name
        )
      `)
      .order('updated_at', { ascending: false });

    if (searchQuery) {
      query = query.ilike('title', `%${searchQuery}%`);
    }

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching posts:', error);
      setLoading(false);
      return;
    }

    if (data) {
      const postsWithCategories = await Promise.all(
        data.map(async (post: any) => {
          const { data: postCategories } = await supabase
            .from('blog_post_categories')
            .select('blog_categories (name)')
            .eq('post_id', post.id);

          const categories = postCategories?.map((pc: any) => pc.blog_categories.name) || [];

          return {
            id: post.id,
            title: post.title,
            slug: post.slug,
            status: post.status,
            published_at: post.published_at,
            updated_at: post.updated_at,
            author_name: post.user_profiles
              ? `${post.user_profiles.first_name || ''} ${post.user_profiles.last_name || ''}`.trim()
              : 'Inconnu',
            categories
          };
        })
      );

      setPosts(postsWithCategories);
    }

    setLoading(false);
  }

  async function handleDelete(postId: string) {
    const { error } = await supabase
      .from('blog_posts')
      .delete()
      .eq('id', postId);

    if (error) {
      console.error('Error deleting post:', error);
      alert('Erreur lors de la suppression');
    } else {
      fetchPosts();
      setDeleteConfirm(null);
    }
  }

  async function handleDuplicate(postId: string) {
    const { data: original } = await supabase
      .from('blog_posts')
      .select('*')
      .eq('id', postId)
      .single();

    if (original) {
      const { id, slug, created_at, updated_at, ...postData } = original;
      const newSlug = `${slug}-copie-${Date.now()}`;

      const { data: newPost, error } = await supabase
        .from('blog_posts')
        .insert({
          ...postData,
          slug: newSlug,
          title: `${original.title} (Copie)`,
          status: 'draft'
        })
        .select()
        .single();

      if (error) {
        console.error('Error duplicating post:', error);
        alert('Erreur lors de la duplication');
      } else if (newPost) {
        const { data: originalCategories } = await supabase
          .from('blog_post_categories')
          .select('category_id')
          .eq('post_id', postId);

        if (originalCategories && originalCategories.length > 0) {
          await supabase
            .from('blog_post_categories')
            .insert(
              originalCategories.map(c => ({
                post_id: newPost.id,
                category_id: c.category_id
              }))
            );
        }

        const { data: originalTags } = await supabase
          .from('blog_post_tags')
          .select('tag_id')
          .eq('post_id', postId);

        if (originalTags && originalTags.length > 0) {
          await supabase
            .from('blog_post_tags')
            .insert(
              originalTags.map(t => ({
                post_id: newPost.id,
                tag_id: t.tag_id
              }))
            );
        }

        fetchPosts();
      }
    }
  }

  async function handleToggleStatus(postId: string, currentStatus: string) {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    const updates: any = { status: newStatus };

    if (newStatus === 'published' && !posts.find(p => p.id === postId)?.published_at) {
      updates.published_at = new Date().toISOString();
    }

    const { error } = await supabase
      .from('blog_posts')
      .update(updates)
      .eq('id', postId);

    if (error) {
      console.error('Error updating status:', error);
      alert('Erreur lors de la mise à jour');
    } else {
      fetchPosts();
    }
  }

  function getStatusBadge(status: string) {
    switch (status) {
      case 'published':
        return <Badge variant="success">Publié</Badge>;
      case 'scheduled':
        return <Badge variant="warning">Programmé</Badge>;
      case 'draft':
        return <Badge variant="default">Brouillon</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  }

  return (
    <AdminLayout title="Articles du blog" description="Gérez les articles de votre blog">
    <div className="p-8">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Articles du blog</h1>
          <p className="text-gray-600 mt-2">Gérez les articles de votre blog</p>
        </div>
        <Button onClick={() => navigate('/admin/blog/posts/new')}>
          <Plus className="w-5 h-5 mr-2" />
          Nouvel article
        </Button>
      </div>

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Rechercher un article..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les statuts</option>
            <option value="draft">Brouillon</option>
            <option value="scheduled">Programmé</option>
            <option value="published">Publié</option>
          </select>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : posts.length === 0 ? (
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">Aucun article trouvé</p>
            <Button onClick={() => navigate('/admin/blog/posts/new')}>
              Créer le premier article
            </Button>
          </div>
        </Card>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Titre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Statut
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Auteur
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Publication
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Dernière modif.
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map((post) => (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{post.title}</div>
                      <div className="text-sm text-gray-500">/blog/{post.slug}</div>
                      {post.categories.length > 0 && (
                        <div className="mt-1 flex gap-1">
                          {post.categories.slice(0, 2).map((cat, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-100 text-blue-800"
                            >
                              {cat}
                            </span>
                          ))}
                          {post.categories.length > 2 && (
                            <span className="text-xs text-gray-500">
                              +{post.categories.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(post.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {post.author_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {post.published_at
                      ? new Date(post.published_at).toLocaleDateString('fr-FR')
                      : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(post.updated_at).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      {post.status === 'published' && (
                        <button
                          onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                          className="text-gray-600 hover:text-gray-900"
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => navigate(`/admin/blog/posts/${post.id}/edit`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Éditer"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDuplicate(post.id)}
                        className="text-green-600 hover:text-green-900"
                        title="Dupliquer"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(post.id, post.status)}
                        className="text-purple-600 hover:text-purple-900"
                        title={post.status === 'published' ? 'Dépublier' : 'Publier'}
                      >
                        <Calendar className="w-4 h-4" />
                      </button>
                      {deleteConfirm === post.id ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleDelete(post.id)}
                            className="text-red-600 hover:text-red-900 font-medium"
                          >
                            Confirmer
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(null)}
                            className="text-gray-600 hover:text-gray-900"
                          >
                            Annuler
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirm(post.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
    </AdminLayout>
  );
}
